import { SeverityLevel } from '@microsoft/applicationinsights-web';
import {
    Component,
    Event,
    EventEmitter,
    h,
    Host,
    Method,
    Prop,
    Watch,
} from '@stencil/core';
import {
    getApolloThemeFromTheme,
    ThemeType,
} from '@uipath/portal-shell-util';

import { PortalNavServiceReorderEvents } from '../../models/appInsights/events/portal-nav.events';
import { Service } from '../../models/service';
import { Features } from '../../utils/featureFlags/featureFlagConstants';
import { getFeatureFlag } from '../../utils/featureFlags/featureFlags';
import { t } from '../../utils/localization/loc';
import Logger from '../../utils/logger/logger';
import { trackStatsigEvent } from '../../utils/logger/statsig';
import { getAccessToken } from '../../utils/session/sessionStore';
import Logo from './assets/logo.svg';
import LogoRedesign from './assets/logo-redesign.svg';

@Component({
    tag: 'ap-waffle-menu',
    styleUrl: 'ap-waffle-menu.scss',
    shadow: true,
})
export class ApWaffleMenu {
    /**
    * The list of services to display in the menu
    * @memberof ApWaffleMenu
    */
    @Prop() services: Service[] = [];

    /**
     * Theme
     * @internal
     */
    @Prop() theme: ThemeType = 'autoTheme';

    /**
     * Language
     * @internal
     */
    @Prop() language: string = 'en';

    /**
     * Prevents the default behavior of opening the overlay
     *
     * @memberof ApWaffleMenu
     */
    @Prop() preventOverlay: boolean;

    /**
     * Define custom event for icon click
     */
    @Event({
        eventName: 'actionClicked',
        composed: true,
        cancelable: true,
        bubbles: true,
    }) actionClicked: EventEmitter;

    private activeDropdownMenuSurface: HTMLPortalMenuSurfaceElement;

    /**
     * Toggle menu surface visibility
     * @method ApWaffleMenu
     */
    @Method()
    async toggleWaffle() {
        return this.activeDropdownMenuSurface.toggleMenu();
    }

    /**
     * Sets menu surface visible to false
     * @method ApWaffleMenu
     */
    @Method()
    async closeWaffle() {
        return this.activeDropdownMenuSurface?.closeMenu();
    }

    @Watch('preventOverlay')
    async preventOverlayChanged(newValue: boolean) {
        const isMenuOpen = await this.activeDropdownMenuSurface.isOpen();

        if (newValue && isMenuOpen) {
            await this.closeWaffle();
            this.actionClicked.emit(true);
        }
    }

    render() {
        const apolloTheme = getApolloThemeFromTheme(this.theme);
        const enableLeftNavRedesign = getFeatureFlag(Features.EnableLeftNavRedesign.name);
        const leftNavMenuExpandable = getFeatureFlag(Features.EnableLeftNavMenuExpansion.name);

        const canExpand = leftNavMenuExpandable && enableLeftNavRedesign;
        const hasMore = this.services.filter(service => service.isMore).length > 0;

        return (
            <Host class={`${apolloTheme} ${enableLeftNavRedesign ? 'redesign' : ''}`}>
                <button
                    id="waffle-button"
                    aria-haspopup="true"
                    class="waffle-button"
                    onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                            if (canExpand && this.preventOverlay) {
                                return;
                            }
                            this.closeWaffle();
                        }
                    }}
                    onClick={() => {
                        if (canExpand && this.preventOverlay) {
                            this.actionClicked.emit();
                        } else {
                            this.toggleWaffle();
                        }
                    }}
                    aria-expanded="false"
                    data-cy="waffle-button"
                    role="button"
                    aria-label={t('waffle-label')}
                >
                    <portal-custom-icon
                        name="waffle"
                        size="24px"
                        aria-label={t('waffle-label')}
                        theme={this.theme}
                    />
                </button>
                <portal-menu-surface
                    slideTransition={enableLeftNavRedesign}
                    backgroundEffect={enableLeftNavRedesign}
                    data-test="portal-waffle-menu"
                    data-cy="portal-waffle-menu"
                    theme={this.theme}
                    onKeyDown={this.handleKeyDown}
                    onPortalMenuSurfaceOpened={() => {
                        Logger.logCustomEvent(
                            PortalNavServiceReorderEvents.ClickedWaffleMenuToggler,
                            { opened: true },
                            SeverityLevel.Verbose,
                        );
                        trackStatsigEvent(getAccessToken(), PortalNavServiceReorderEvents.ClickedWaffleMenuToggler, 'true');
                    }}
                    onPortalMenuSurfaceClosed={() => {
                        Logger.logCustomEvent(
                            PortalNavServiceReorderEvents.ClickedWaffleMenuToggler,
                            { opened: false },
                            SeverityLevel.Verbose,
                        );
                        trackStatsigEvent(getAccessToken(), PortalNavServiceReorderEvents.ClickedWaffleMenuToggler, 'false');
                    }}
                    ref={(elem) => this.activeDropdownMenuSurface = elem as HTMLPortalMenuSurfaceElement}
                >
                    <div class="waffle-menu-content waffle-menu-res">
                        <div class="waffle-header">
                            {
                                enableLeftNavRedesign && (
                                    <div class="waffle-header-close">
                                        <button
                                            id="waffle-button-close"
                                            aria-haspopup="true"
                                            class="waffle-button waffle-button-close"
                                            onClick={() => this.closeWaffle()}
                                            data-test="portal-waffle-menu-close-btn"
                                            data-cy="portal-waffle-menu-close-btn"
                                            role="button"
                                            aria-label={t('waffle-label')}
                                        >
                                            <portal-custom-icon
                                                name="waffle"
                                                size="24px"
                                                aria-label={t('waffle-label')}
                                                theme={this.theme}
                                            />
                                        </button>
                                    </div>
                                )
                            }

                            <span
                                class="logo-container"
                                data-test="portal-waffle-menu-logo"
                                data-cy="portal-waffle-menu-logo"
                                aria-label={t('aria-label-logo')}
                                id="logo"
                                innerHTML={enableLeftNavRedesign ? LogoRedesign : Logo} />
                            {
                                !enableLeftNavRedesign && (
                                    <portal-icon-button
                                        name="close"
                                        data-test="portal-waffle-menu-close-btn"
                                        data-cy="portal-waffle-menu-close-btn"
                                        size="sm"
                                        theme={this.theme}
                                        onClick={() => this.closeWaffle()} />
                                )
                            }
                        </div>
                        <div class="waffle-items-list apollo-scrollbar" data-cy="waffle-items-list" role="list">
                            { ...this.getMenuItems(true, hasMore) }
                            { hasMore && [
                                <portal-divider
                                    type="horizontal"
                                    ignoreWidthMargins={!enableLeftNavRedesign}
                                    bothMarginSize={enableLeftNavRedesign}
                                    marginSize="xxxl"
                                    horizontalMarginSize="h-xxl"
                                />,
                                <h4 key='h4' class={`more-products${enableLeftNavRedesign ? ' redesign' : ''}`}>{t('more_products', this.language)}</h4>,
                                ...this.getMenuItems(false),
                            ]}
                        </div>
                    </div>
                </portal-menu-surface>
            </Host>
        );
    }

    private handleKeyDown(event: KeyboardEvent) {
        if (event.key === 'Escape') {
            this.closeWaffle();
        }
    }

    private getMenuItems = (core: boolean, hasMore: boolean = true) => {
        const services = this.services.filter(service => core ? !service.isMore : service.isMore);

        return services?.map((button, index) => (
            <portal-service-tile
                {...button}
                data-test={'nav-tile-' + button.id}
                serviceid={button.id}
                role='listitem'
                isLast={index === services.length - 1 && ((core && !hasMore) || !core)}
                closeCallback={() => this.closeWaffle()}
                onClick={() => {
                    if (button.handled) {
                        this.closeWaffle();
                    }
                }}
                listMode={true}
            />
        ));
    };
}
